import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, CardActions, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';
import { SectionHeader, ProgressBar } from '../components/ui';
import { ROUTE_STATUS, PRIORITY, ROUTE_KIND } from '../utils/schema';
import { createCvProfileForRoute } from '../utils/cvIntegration';

export default function RoutesPage({ selectedRouteId, onSelectRoute }) {
  const routes = useGrowthPlannerStore((s) => s.routes);
  const learningResources = useGrowthPlannerStore((s) => s.learningResources);
  const addRoute = useGrowthPlannerStore((s) => s.addRoute);
  const deleteRoute = useGrowthPlannerStore((s) => s.deleteRoute);
  const updateRoute = useGrowthPlannerStore((s) => s.updateRoute);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', objective: '', targetRole: '',
    targetDate: '', priority: 'medium', estimatedHours: '', routeKind: 'standard',
  });

  const handleCreate = () => {
    if (!form.name.trim()) return;
    addRoute({
      ...form,
      estimatedHours: Number(form.estimatedHours) || 0,
    });
    setForm({ name: '', description: '', objective: '', targetRole: '', targetDate: '', priority: 'medium', estimatedHours: '', routeKind: 'standard' });
    setDialogOpen(false);
  };

  const handleCreateCv = async (route) => {
    try {
    const profileId = await createCvProfileForRoute(route, learningResources);
    updateRoute(route.id, { profileId });
    } catch(e) { window.alert(e.response?.data?.message||e.message); }
  };

  return (
    <Box>
      <SectionHeader
        title="Rutas Profesionales"
        subtitle="Organiza tu crecimiento hacia distintos objetivos de carrera"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Nueva Ruta
          </Button>
        }
      />

      {routes.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" gutterBottom>
            Ej: Gerencia BI, Product Manager, Arquitecto de Software, UX Designer…
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 2 }}>
            Crear primera ruta
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {routes.map((route) => (
            <Grid item xs={12} sm={6} lg={4} key={route.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: selectedRouteId === route.id ? 'primary.main' : 'divider',
                  borderWidth: selectedRouteId === route.id ? 2 : 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>{route.name}</Typography>
                    <Chip label={ROUTE_STATUS[route.status]?.label} size="small" color={ROUTE_STATUS[route.status]?.color} />
                  </Stack>
                  {route.targetRole && (
                    <Typography variant="body2" color="primary" fontWeight={500}>{route.targetRole}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    {route.description || route.objective || 'Sin descripción'}
                  </Typography>
                  <ProgressBar value={route.progress || 0} label="Progreso" />
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip label={PRIORITY[route.priority]?.label} size="small" variant="outlined" />
                    <Chip label={`${route.stages?.length || 0} etapas`} size="small" variant="outlined" />
                    <Chip label={`${route.investedHours || 0}h invertidas`} size="small" variant="outlined" />
                  </Stack>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => onSelectRoute(route.id)}>
                      Detalle
                    </Button>
                    {!route.profileId && (
                      <Button size="small" onClick={() => handleCreateCv(route)}>
                        Crear CV
                      </Button>
                    )}
                  </Stack>
                  <IconButton size="small" color="error" onClick={() => deleteRoute(route.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Ruta Profesional</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nombre" placeholder="Ej: Gerencia de Business Intelligence" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField select label="Tipo de ruta" value={form.routeKind} onChange={(e) => setForm({ ...form, routeKind: e.target.value })} fullWidth>
              {Object.entries(ROUTE_KIND).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
            <TextField label="Cargo / meta deseada" placeholder="Ej: BI Manager" value={form.targetRole} onChange={(e) => setForm({ ...form, targetRole: e.target.value })} fullWidth />
            <TextField label="Objetivo profesional" multiline rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} fullWidth />
            <TextField label="Descripción" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Fecha objetivo" type="date" value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField select label="Prioridad" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} fullWidth>
                {Object.entries(PRIORITY).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label="Horas estimadas" type="number" value={form.estimatedHours} onChange={(e) => setForm({ ...form, estimatedHours: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name.trim()}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
